/**
 * The handful of FAQ answers the landing page shows inline.
 *
 * Real content from `GET /api/cms/faq`, not a hard-coded sample. The landing
 * page is the one place a visitor decides whether to bother, and the questions
 * they actually ask are already in the CMS — copying three of them into the
 * source would make the front door drift away from the help pages the moment
 * anyone edits either.
 *
 * The endpoint returns `[]` in preview mode and can be empty on a fresh
 * deployment, so the section that consumes this must render nothing rather
 * than an empty accordion.
 */
import { api } from '../../core/network/api';

export interface FaqPreviewEntry {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
}

/** How many to show before sending people to the full help page. */
export const FAQ_PREVIEW_LIMIT = 4;

/** Coerce the CMS payload; anything malformed is dropped, never rendered raw. */
export function parseFaqEntries(raw: unknown): readonly FaqPreviewEntry[] {
  if (!Array.isArray(raw)) return [];
  const entries: FaqPreviewEntry[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const record = item as Record<string, unknown>;
    const question = typeof record.question === 'string' ? record.question.trim() : '';
    const answer = typeof record.answer === 'string' ? record.answer.trim() : '';
    if (question === '' || answer === '') continue;
    entries.push({
      id: typeof record.id === 'string' ? record.id : `faq-${entries.length}`,
      question,
      answer,
    });
  }
  return entries.slice(0, FAQ_PREVIEW_LIMIT);
}

let cached: readonly FaqPreviewEntry[] | null = null;
let inFlight: Promise<readonly FaqPreviewEntry[]> | null = null;

/** Never rejects: an unreachable CMS costs the section, not the page. */
export async function loadFaqPreview(): Promise<readonly FaqPreviewEntry[]> {
  if (cached !== null) return cached;
  if (inFlight !== null) return inFlight;
  inFlight = api
    .get<unknown>('/cms/faq')
    .then((raw) => parseFaqEntries(raw))
    .catch(() => [] as readonly FaqPreviewEntry[])
    .then((entries) => {
      cached = entries;
      inFlight = null;
      return entries;
    });
  return inFlight;
}

/** The cached list, or `null` when nothing has resolved. Never fetches. */
export function peekFaqPreview(): readonly FaqPreviewEntry[] | null {
  return cached;
}

/** Test seam: drop the module-level cache. */
export function resetFaqPreviewCacheForTests(): void {
  cached = null;
  inFlight = null;
}
